/**
 * XP Store - Zustand state management for XP system
 *
 * Manages:
 * - Per-language XP summary (level, tier, progress, daily/weekly totals)
 * - Global XP total across all languages
 * - XP history for charts
 * - Recent XP gain events for toast notifications
 */

import { create } from 'zustand';
import { xpService, checkMilestones } from '@/services/xp';
import { getSafeUserId } from '@/utils/auth-utils';
import { useLearningStore } from './learningStore';
import type {
  XpSummary,
  XpHistoryPoint,
  XpEvent,
  XpEventType,
  XpSourceType,
} from '@/types/xp';
import { XP_EVENT_LABELS, levelFromXp, tierFromLevel } from '@/types/xp';

// ============================================================================
// Types
// ============================================================================

export type XpToastVariant = 'regular' | 'level-up' | 'milestone';

export interface RecentXpGain {
  id: string;
  amount: number;
  reason: string;
  eventType: XpEventType;
  variant: XpToastVariant;
  /** New level (only for level-up variant) */
  newLevel?: number;
  /** Tier name (only for level-up variant) */
  tierName?: string;
  /** Tier color (only for level-up variant) */
  tierColor?: string;
  /** Milestone title (only for milestone variant) */
  milestoneTitle?: string;
}

export interface XpState {
  // Per-language XP summary
  xpSummary: XpSummary | null;
  // Global XP total across all languages
  globalXp: number;
  // XP history for charts
  xpHistory: XpHistoryPoint[];
  // Recent XP events for activity feed
  recentEvents: XpEvent[];
  // All-language summaries for per-language breakdown
  allLanguageSummaries: XpSummary[];
  // Recent XP gains for toast display (stacking, max 3)
  recentXpGains: RecentXpGain[];
  /** @deprecated Use recentXpGains[0] instead */
  recentXpGain: RecentXpGain | null;
  // Loading states
  isLoading: boolean;
  isLoadingHistory: boolean;
  isLoadingEvents: boolean;
  // Last fetched language to detect changes
  lastFetchedLanguage: string | null;
  // Last fetched user id to detect account switches mid-session
  lastFetchedUserId: string | null;
  // Last fetched timestamp to detect staleness
  lastFetchedAt: number;
  // Error state
  error: string | null;

  // Actions
  fetchXpSummary: (language?: string) => Promise<void>;
  fetchXpHistory: (language?: string, days?: number) => Promise<void>;
  fetchRecentEvents: (language?: string, limit?: number) => Promise<void>;
  fetchAllLanguageSummaries: () => Promise<void>;
  awardXp: (
    eventType: XpEventType,
    baseXp: number,
    multiplier?: number,
    sourceId?: string | null,
    sourceType?: XpSourceType,
    metadata?: Record<string, unknown>
  ) => Promise<void>;
  showXpGain: (amount: number, reason: string, eventType: XpEventType, variant?: XpToastVariant, extra?: Partial<RecentXpGain>) => void;
  dismissXpGain: (id: string) => void;
  clearRecentXpGain: () => void;
  clearError: () => void;
  reset: () => void;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useXpStore = create<XpState>()((set, get) => ({
  // Initial state
  xpSummary: null,
  globalXp: 0,
  xpHistory: [],
  recentEvents: [],
  allLanguageSummaries: [],
  recentXpGains: [],
  recentXpGain: null,
  isLoading: false,
  isLoadingHistory: false,
  isLoadingEvents: false,
  lastFetchedLanguage: null,
  lastFetchedUserId: null,
  lastFetchedAt: 0,
  error: null,

  // ========================================================================
  // Fetch XP Summary
  // ========================================================================

  fetchXpSummary: async (language?: string) => {
    const userId = getSafeUserId();
    const languageCode = language ?? useLearningStore.getState().currentLanguage;

    if (!userId || !languageCode) {
      set({ error: 'Not authenticated or no language selected', isLoading: false });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      // Fetch per-language summary and global total in parallel
      const [summaryResult, globalResult] = await Promise.all([
        xpService.getUserXpSummary(userId, languageCode),
        xpService.getGlobalXpTotal(userId),
      ]);

      if (summaryResult.error) throw new Error(summaryResult.error);
      if (globalResult.error) throw new Error(globalResult.error);

      // Take the first summary (per-language query returns one row)
      const summary = summaryResult.data.length > 0 ? summaryResult.data[0] : null;

      set({
        xpSummary: summary,
        globalXp: globalResult.data,
        isLoading: false,
        lastFetchedLanguage: languageCode,
        lastFetchedUserId: userId,
        lastFetchedAt: Date.now(),
      });
    } catch (error) {
      console.error('Failed to fetch XP summary:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch XP summary',
        isLoading: false,
      });
    }
  },

  // ========================================================================
  // Fetch XP History
  // ========================================================================

  fetchXpHistory: async (language?: string, days: number = 30) => {
    const userId = getSafeUserId();
    const languageCode = language ?? useLearningStore.getState().currentLanguage;

    if (!userId || !languageCode) {
      return;
    }

    set({ isLoadingHistory: true });

    try {
      const { data, error } = await xpService.getXpHistory(userId, languageCode, days);

      if (error) throw new Error(error);

      set({
        xpHistory: data,
        isLoadingHistory: false,
      });
    } catch (error) {
      console.error('Failed to fetch XP history:', error);
      set({ isLoadingHistory: false });
    }
  },

  // ========================================================================
  // Fetch Recent Events
  // ========================================================================

  fetchRecentEvents: async (language?: string, limit: number = 20) => {
    const userId = getSafeUserId();
    const languageCode = language ?? useLearningStore.getState().currentLanguage;

    if (!userId || !languageCode) return;

    set({ isLoadingEvents: true });

    try {
      const { data, error } = await xpService.getRecentXpEvents(userId, languageCode, limit);
      if (error) throw new Error(error);
      set({ recentEvents: data, isLoadingEvents: false });
    } catch (error) {
      console.error('Failed to fetch recent XP events:', error);
      set({ isLoadingEvents: false });
    }
  },

  // ========================================================================
  // Fetch All Language Summaries
  // ========================================================================

  fetchAllLanguageSummaries: async () => {
    const userId = getSafeUserId();
    if (!userId) return;

    try {
      const { data, error } = await xpService.getAllLanguageXpSummaries(userId);
      if (error) throw new Error(error);
      set({ allLanguageSummaries: data });
    } catch (error) {
      console.error('Failed to fetch all language summaries:', error);
    }
  },

  // ========================================================================
  // Award XP
  // ========================================================================

  awardXp: async (
    eventType: XpEventType,
    baseXp: number,
    multiplier: number = 1.0,
    sourceId?: string | null,
    sourceType?: XpSourceType,
    metadata: Record<string, unknown> = {}
  ) => {
    const userId = getSafeUserId();
    const languageCode = useLearningStore.getState().currentLanguage;

    if (!userId || !languageCode) return;

    try {
      const { data: event, error } = await xpService.awardXp(
        userId,
        languageCode,
        eventType,
        baseXp,
        multiplier,
        sourceId,
        sourceType,
        metadata
      );

      if (error) {
        console.error('Failed to award XP:', error);
        return;
      }

      if (event) {
        const reason = XP_EVENT_LABELS[eventType];
        const currentSummary = get().xpSummary;
        const previousLevel = currentSummary?.currentLevel ?? 0;
        const previousTotalXp = currentSummary?.totalXp ?? 0;
        const newTotalXp = previousTotalXp + event.finalXp;
        const newLevel = levelFromXp(newTotalXp);

        // Detect level-up
        if (newLevel > previousLevel && previousLevel > 0) {
          const tier = tierFromLevel(newLevel);
          get().showXpGain(event.finalXp, reason, eventType, 'level-up', {
            newLevel,
            tierName: tier.name,
            tierColor: tier.color,
          });
        } else {
          // Regular XP toast
          get().showXpGain(event.finalXp, reason, eventType);
        }

        // Optimistically update xpSummary if we have one
        if (currentSummary) {
          set({
            xpSummary: {
              ...currentSummary,
              totalXp: newTotalXp,
              currentLevel: newLevel,
              xpToday: currentSummary.xpToday + event.finalXp,
              xpThisWeek: currentSummary.xpThisWeek + event.finalXp,
            },
            globalXp: get().globalXp + event.finalXp,
          });
        }

        // Check milestones (fire-and-forget, uses available context)
        try {
          const updatedSummary = get().xpSummary;
          const allLangSummaries = get().allLanguageSummaries;
          if (updatedSummary) {
            const milestoneContext = {
              level: updatedSummary.currentLevel,
              totalXp: updatedSummary.totalXp,
              streakDays: 0, // Not tracked in XP store; milestones relying on this fire from other sources
              messagesCount: 0, // Not tracked in XP store
              scenariosCompleted: 0, // Not tracked in XP store
              languageCount: allLangSummaries.length || 1,
              srsCardsCount: 0, // Not tracked in XP store
              weeklyXp: updatedSummary.xpThisWeek,
            };
            const newMilestones = checkMilestones(userId, milestoneContext);
            for (const milestone of newMilestones) {
              get().showXpGain(0, milestone.description, eventType, 'milestone', {
                milestoneTitle: milestone.title,
              });
            }
          }
        } catch {
          // Milestone checking is non-critical
        }

        // Refetch summary to get authoritative level/tier from DB
        // (fire-and-forget to avoid blocking toast display)
        get().fetchXpSummary(languageCode);
      }
    } catch (error) {
      console.error('Error in awardXp:', error);
    }
  },

  // ========================================================================
  // Toast Management
  // ========================================================================

  showXpGain: (
    amount: number,
    reason: string,
    eventType: XpEventType,
    variant: XpToastVariant = 'regular',
    extra: Partial<RecentXpGain> = {}
  ) => {
    const toast: RecentXpGain = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      amount,
      reason,
      eventType,
      variant,
      ...extra,
    };

    const current = get().recentXpGains;
    // Keep max 3 toasts, newest at end
    const updated = [...current, toast].slice(-3);
    set({
      recentXpGains: updated,
      recentXpGain: updated[updated.length - 1] ?? null,
    });
  },

  dismissXpGain: (id: string) => {
    const updated = get().recentXpGains.filter((g) => g.id !== id);
    set({
      recentXpGains: updated,
      recentXpGain: updated[updated.length - 1] ?? null,
    });
  },

  clearRecentXpGain: () => {
    set({ recentXpGains: [], recentXpGain: null });
  },

  // ========================================================================
  // State Management
  // ========================================================================

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      xpSummary: null,
      globalXp: 0,
      xpHistory: [],
      recentEvents: [],
      allLanguageSummaries: [],
      recentXpGains: [],
      recentXpGain: null,
      isLoading: false,
      isLoadingHistory: false,
      isLoadingEvents: false,
      lastFetchedLanguage: null,
      lastFetchedUserId: null,
      lastFetchedAt: 0,
      error: null,
    });
  },
}));
